import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useRegisterAdminDirty } from "@/features/admin-dashboard/admin-unsaved-changes-context"

import { diffJson, isNoOpDiff } from "./json-diff"
import { JsonEditor } from "./json-editor"
import { RawJsonConfirmDialog } from "./raw-json-confirm-dialog"

type EditorChange = {
  text: string
  parsed: unknown
  error: string | null
  isValid: boolean
}

type RawJsonTabProps = {
  /** The current committed value, loaded from the server. */
  value: unknown
  /**
   * Short, human-readable label of what's being edited. Used in the warning
   * banner and the confirm dialog (e.g., "explorer middleware", "model
   * gpt-4o").
   */
  resourceLabel: string
  /**
   * String the admin must type verbatim in the confirm dialog. Usually the
   * resource id (e.g., the model id, library id, or agent name). Lower-cases
   * are treated case-sensitively.
   */
  confirmToken: string
  /**
   * Callback invoked when the admin confirms the save. Receives the newly
   * parsed value and the reason the admin entered in the confirm dialog.
   * Implementations should call the relevant admin API mutation and throw on
   * failure — the confirm dialog will surface the error.
   */
  onSave: (next: unknown, reason: string) => Promise<void>
  /** True while the caller's save mutation is in flight. */
  isPending?: boolean
  /**
   * Bump this number to force the JSON editor to re-sync its text buffer to
   * ``value`` (e.g., after a successful save or a "Reload from server"
   * button). Without this, the buffer is otherwise only initialised once per
   * ``value`` identity.
   */
  resetSignal?: number
}

/**
 * Reusable "Raw JSON" tab content: a warning banner, the JSON editor, and the
 * Save button that opens the mandatory confirm dialog.
 *
 * This component is designed to slot into any ``Tabs`` component inside an
 * existing admin edit dialog. It owns the confirm dialog state but does NOT
 * own the parent dialog — closing the parent unmounts this and resets all
 * local state.
 *
 * Intentionally dependency-light: only uses primitives already in the design
 * system + the shared ``JsonEditor`` and ``RawJsonConfirmDialog``.
 */
export function RawJsonTab({
  value,
  resourceLabel,
  confirmToken,
  onSave,
  isPending = false,
  resetSignal,
}: RawJsonTabProps) {
  const { t } = useTranslation()
  const [editorState, setEditorState] = useState<EditorChange>(() => ({
    text: "",
    parsed: value,
    error: null,
    isValid: true,
  }))
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Reset pending edits when the parent reloads (e.g., after save or
  // explicit reload). Re-sync to the current server value.
  useEffect(() => {
    setEditorState({ text: "", parsed: value, error: null, isValid: true })
    setConfirmOpen(false)
  }, [value, resetSignal])

  const handleEditorChange = useCallback((change: EditorChange) => {
    setEditorState(change)
  }, [])

  const proposedValue = editorState.isValid ? editorState.parsed : value
  const diff = useMemo(() => diffJson(value, proposedValue), [value, proposedValue])
  const dirty = !isNoOpDiff(diff)
  useRegisterAdminDirty(`raw-json:${resourceLabel}:${confirmToken}`, dirty)
  const canSave = editorState.isValid && dirty && !isPending

  const handleSave = async (reason: string) => {
    // Parent owns the mutation — we just forward the parsed value and reason.
    // On success the parent should bump resetSignal or re-pass the new value.
    await onSave(proposedValue, reason)
    setConfirmOpen(false)
  }

  return (
    <div className="flex flex-col gap-3" data-admin-dirty={dirty ? "true" : undefined}>
      <Alert variant="destructive">
        <AlertTitle>
          {t("adminDashboard.rawJson.warningTitle", { defaultValue: "Raw JSON — advanced" })}
        </AlertTitle>
        <AlertDescription>
          {t("adminDashboard.rawJson.warningDescription", {
            defaultValue:
              "Changes bypass the Form tab's validation and take effect immediately. Prefer the Form tab for any field that has a dedicated control. Every raw save is recorded in the audit trail with the reason you provide below.",
          })}
        </AlertDescription>
      </Alert>

      <JsonEditor resetSignal={resetSignal} rows={16} value={value} onChange={handleEditorChange} />

      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-xs">
          {dirty
            ? t("adminDashboard.rawJson.diffSummary", {
                defaultValue: "{{added}} added · {{removed}} removed",
                added: diff.summary.added,
                removed: diff.summary.removed,
              })
            : t("adminDashboard.rawJson.noChanges", { defaultValue: "No changes yet" })}
        </p>
        <Button
          disabled={!canSave}
          type="button"
          variant="destructive"
          onClick={() => setConfirmOpen(true)}
        >
          {t("adminDashboard.rawJson.reviewAndSave", { defaultValue: "Review & save…" })}
        </Button>
      </div>

      <RawJsonConfirmDialog
        after={proposedValue}
        before={value}
        confirmToken={confirmToken}
        isOpen={confirmOpen}
        isPending={isPending}
        resourceLabel={resourceLabel}
        onConfirm={handleSave}
        onOpenChange={setConfirmOpen}
      />
    </div>
  )
}
